/* bf-sample-landing.js — claim flow for sections/bf-sample-landing.liquid.
 *
 * Only rendered when the section has verified a $0, in-stock, one-time offer
 * variant. Everything is scoped to [data-bfs-root]; no globals, no listeners
 * outside the page root (plus one pageshow to recover from back navigation).
 *
 * Flow, using Shopify's documented Ajax Cart API and native checkout:
 *   1. GET cart.js (nothing is mutated on page load, only on a click)
 *   2. sample already in cart  -> no second add; straight to checkout
 *      other items in cart     -> local review step; nothing is removed,
 *                                 replaced or re-quantified
 *      empty cart              -> add 1 sample pack, go to checkout
 *   3. POST cart/add.js { id, quantity: 1 } then location.assign('/checkout')
 *
 * Never calls cart/clear or cart/change. A single in-flight lock across both
 * CTAs stops rapid taps from adding twice. This is UI protection only; it is
 * not a per-customer campaign limit.
 */
(function () {
  'use strict';

  var TIMEOUT_MS = 15000;

  function init(root) {
    if (root.__bfsReady) return;
    root.__bfsReady = true;

    var variantId = Number(root.getAttribute('data-variant-id'));
    var cartUrl = root.getAttribute('data-cart-url');
    var addUrl = root.getAttribute('data-cart-add-url');
    var checkoutUrl = root.getAttribute('data-checkout-url');
    var forms = Array.prototype.slice.call(root.querySelectorAll('[data-bfs-form]'));
    var busy = false;

    if (!variantId || !cartUrl || !addUrl || !checkoutUrl || !forms.length) return;

    function q(form, sel) { return form.querySelector(sel); }

    function setBusy(on) {
      busy = on;
      forms.forEach(function (form) {
        var btns = form.querySelectorAll('[data-bfs-cta], [data-bfs-confirm]');
        Array.prototype.forEach.call(btns, function (b) {
          b.disabled = on;
          if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy');
        });
        var label = q(form, '[data-bfs-label]');
        if (label) label.textContent = on ? 'One sec…' : 'Claim my samples';
      });
    }

    function hideMessages(form) {
      q(form, '[data-bfs-error]').hidden = true;
      q(form, '[data-bfs-review]').hidden = true;
    }

    function showError(form, msg) {
      var el = q(form, '[data-bfs-error]');
      el.textContent = msg;
      el.hidden = false;
    }

    function request(url, opts) {
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS) : null;
      opts = opts || {};
      opts.headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      opts.credentials = 'same-origin';
      opts.cache = 'no-store';
      if (ctrl) opts.signal = ctrl.signal;
      return fetch(url, opts).then(function (res) {
        if (timer) clearTimeout(timer);
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok) {
            var err = new Error(body.description || body.message || 'Request failed');
            err.status = res.status;
            throw err;
          }
          return body;
        });
      }, function (e) {
        if (timer) clearTimeout(timer);
        throw e;
      });
    }

    function goToCheckout() {
      // Keep the lock held: the page is unloading. pageshow releases it if
      // the visitor comes back.
      window.location.assign(checkoutUrl);
    }

    function addAndCheckout(form) {
      return request(addUrl, {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      }).then(goToCheckout);
    }

    function fail(form, err) {
      setBusy(false);
      var msg = err && (err.status === 422 || err.status === 404)
        ? 'These samples are unavailable right now. Nothing was added to your cart.'
        : 'Something went wrong and nothing was added to your cart. Please try again.';
      showError(form, msg);
      var btn = q(form, '[data-bfs-cta]');
      if (btn) btn.focus();
    }

    function onClaim(form) {
      if (busy) return;
      hideMessages(form);
      setBusy(true);

      request(cartUrl).then(function (cart) {
        var items = (cart && cart.items) || [];
        var hasSample = items.some(function (i) { return Number(i.variant_id) === variantId; });

        // Already claimed in this cart: never increment, just continue.
        if (hasSample) { goToCheckout(); return; }

        var others = items.reduce(function (n, i) { return n + (Number(i.quantity) || 0); }, 0);
        if (others > 0) {
          setBusy(false);
          var review = q(form, '[data-bfs-review]');
          var text = q(form, '[data-bfs-review-text]');
          text.textContent = 'Your cart already has ' + others + (others === 1 ? ' item' : ' items') +
            '. They stay in your order, so your checkout will include them as well as the free samples. ' +
            'Your total and shipping may differ from a samples-only order.';
          review.hidden = false;
          text.focus();
          return;
        }

        return addAndCheckout(form);
      }).catch(function (err) { fail(form, err); });
    }

    function onConfirm(form) {
      if (busy) return;
      q(form, '[data-bfs-error]').hidden = true;
      setBusy(true);
      // Re-read the cart: a second tab may have added the sample meanwhile.
      request(cartUrl).then(function (cart) {
        var items = (cart && cart.items) || [];
        if (items.some(function (i) { return Number(i.variant_id) === variantId; })) { goToCheckout(); return; }
        return addAndCheckout(form);
      }).catch(function (err) { fail(form, err); });
    }

    forms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        onClaim(form);
      });
      var confirm = q(form, '[data-bfs-confirm]');
      if (confirm) confirm.addEventListener('click', function () { onConfirm(form); });
    });

    // Back/forward cache restores the page with the lock still held.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        setBusy(false);
        forms.forEach(hideMessages);
      }
    });
  }

  function boot() {
    var roots = document.querySelectorAll('.bf-sample-lp[data-bfs-root]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
